import {
    api,
    apiClient,
    config,
    debug,
    form,
    formHelpers,
    Formsy,
    i18n,
    inject,
    observable,
    observer,
    pr,
    React,
    RouteComponentProps,
    theme,
    ui,
} from "aaa";
import { Attachments } from "models/Attachment";
import { Loan } from "models/Loan";
import { User } from "models/User";
import * as ReactDOMServer from "react-dom/server";
import {
    BaseDataContent,
    BaseDataForm,
    BaseDataSecondBorrower,
} from "./BaseDataForm";
import { ContactContent, ContactForm } from "./ContactForm";
import { DocumentsContent, DocumentsForm } from "./DocumentsForm";
import { ExpensesContent, ExpensesForm } from "./ExpensesForm";
import { IncomeContent, IncomeForm, IncomeSecondBorrower } from "./IncomeForm";
import { LoanParameters, LoanResult } from "./LoanCalculator";
import { Mail } from "./Mail";
import {
    EmploymentComponent,
    PersonalDataContent,
    PersonalDataForm,
} from "./PersonalDataForm";
import { FormsyCheckboxWithValidation } from "./util/FormsyCheckboxWithValidation";

// Open a popup centered on screen (https://stackoverflow.com/a/16861050/677910)
function popupCenter(url: string, title: string, w: number, h: number) {
    // Fixes dual-screen position                         Most browsers      Firefox
    const dualScreenLeft =
        window.screenLeft !== undefined ? window.screenLeft : window.screenX;
    const dualScreenTop =
        window.screenTop !== undefined ? window.screenTop : window.screenY;

    const width = window.innerWidth
        ? window.innerWidth
        : document.documentElement.clientWidth
            ? document.documentElement.clientWidth
            : screen.width;
    const height = window.innerHeight
        ? window.innerHeight
        : document.documentElement.clientHeight
            ? document.documentElement.clientHeight
            : screen.height;

    const left = width / 2 - w / 2 + dualScreenLeft;
    const top = height / 2 - h / 2 + dualScreenTop;
    const newWindow = window.open(
        url,
        title,
        "scrollbars=yes, width=" +
        w +
        ", height=" +
        h +
        ", top=" +
        top +
        ", left=" +
        left
    );

    // Puts focus on the newWindow
    if (window.focus && newWindow) {
        newWindow.focus();
    }
}

type IProps = RouteComponentProps<{}> &
    i18n.InjectedIntlProps & {
        user: User;
        loan: Loan;
        attachments: Attachments;
    };

let formWasSubmitted = false;

@i18n.injectIntl
@inject("user", "loan", "attachments")
@observer
export class Summary extends React.Component<IProps, {}> {
    @observable
    formIsValid = false;

    @observable
    sendingMail = false;

    @observable
    invalidSubmit = false;

    private formRef: any;

    componentDidMount() {
        window.scrollTo(0, 0);

        // SUCCESS-REDIRECT: user has already fully submitted previously, hard redirect to /loan
        if (this.props.user.successfullySubmitted === true) {
            window.location.replace(
                window.location.origin + window.location.pathname
            ); // we wont include hash - root page only.
        }
    }

    onSubmit = async (fields: any, resetForm: any, invalidateForm: any) => {
        formWasSubmitted = true;

        const user = this.props.user;
        BaseDataForm.parseInput(user, fields);
        PersonalDataForm.parseInput(user, fields);
        ContactForm.parseInput(user, fields);
        IncomeForm.parseInput(user, fields);
        ExpensesForm.parseInput(user, fields);
        DocumentsForm.parseInput(user, fields);

        // continue here -> why not true?
        user.gdprAccepted = fields.gdpr;
        user.wantsClimateBonus = fields.wantsClimateBonus;

        debug.log("### Summary", this.props.user);

        if (this.props.attachments.attachments.length === 0) {
            ui.showMessageBox({
                title: ui.__("summary.attachments"),
                message: ui.__("summary.attachmentsMising"),
            });
        } else {
            try {
                this.sendingMail = true;
                await this.sendMail();
                this.sendingMail = false;

                this.props.history.push("/success");

                // successful submission, clear attachments as those were now erased on the server
                // therefore fully reset only this provided store.
                this.props.attachments.reset();
                this.props.user.successfullySubmitted = true;
            } catch (err) {
                console.error(err);
                this.sendingMail = false;
                if (err.statusCode === 409) {
                    ui.showMessageBox({
                        title: ui.__("error"),
                        message: ui.__("error.couldNotSendEmail", {
                            email: user.emailAddress,
                        }),
                    });
                } else {
                    ui.showGenericError(err.status);
                }
            }
        }
    }

    sendMail = () => {
        const request = api.postLoan(
            ReactDOMServer.renderToStaticMarkup(
                <Mail user={this.props.user} loan={this.props.loan} />
            ),
            this.props.loan,
            this.props.user,
            this.props.attachments
        );

        return apiClient.request(request);
    }

    renderLoanSummary() {
        return (
            <pr.FormContainer>
                <pr.Title>{ui.__("loanSummary")}</pr.Title>
                <br />
                <LoanParameters
                    amount={this.props.loan.amount}
                    duration={this.props.loan.durationInYears}
                />
                <LoanResult
                    totalAmount={this.props.loan.totalAmount}
                    interestRate={this.props.loan.interestRate}
                    interestRateEffective={
                        this.props.loan.interestRateEffective
                    }
                    monthlyRate={this.props.loan.monthlyRate}
                />
            </pr.FormContainer>
        );
    }

    render() {
        return (
            <Formsy.Form
                onInvalidSubmit={(fields: any, reset: any, invalidate: any) => {
                    this.invalidSubmit = true;
                    formHelpers.invalidateRequiredFields(
                        this.formRef,
                        invalidate
                    );
                }}
                onValidSubmit={(
                    fields: any,
                    resetForm: any,
                    invalidateForm: any
                ) => {
                    this.invalidSubmit = false;
                    this.onSubmit(fields, resetForm, invalidateForm);
                }}
                onValid={() => {
                    this.formIsValid = true;
                }}
                onInvalid={() => {
                    this.formIsValid = false;
                }}
                noValidate
                ref={(c: any) => (this.formRef = c)}
            >
                {this.renderLoanSummary()}

                <pr.Separator />
                <pr.FormContainer>
                    <pr.Title>{ui.__("header.baseData")}</pr.Title>
                    <BaseDataContent user={this.props.user} isSummary={true} />
                </pr.FormContainer>

                <pr.Separator />
                <pr.FormContainer>
                    <PersonalDataContent
                        user={this.props.user}
                        isSummary={true}
                    />
                </pr.FormContainer>

                <pr.Separator />
                <pr.FormContainer>
                    <pr.Title>{ui.__("header.contactInformation")}</pr.Title>
                    <ContactContent user={this.props.user} />
                </pr.FormContainer>

                <pr.Separator />
                <pr.FormContainer>
                    <pr.Title>{ui.__("header.income")}</pr.Title>
                    <IncomeContent user={this.props.user} isSummary={true} />
                </pr.FormContainer>

                <pr.Separator />
                <pr.FormContainer>
                    <pr.Title>{ui.__("header.expenses")}</pr.Title>
                    <ExpensesContent user={this.props.user} />
                </pr.FormContainer>

                {this.props.user.hasSecondBorrower && (
                    <div>
                        <pr.Separator />
                        <pr.FormContainer>
                            <pr.Title>
                                {ui.__("header.secondBorrower")}
                            </pr.Title>
                            <pr.Subtitle>
                                {ui.__("header.baseData")}
                            </pr.Subtitle>
                            <BaseDataSecondBorrower user={this.props.user} />
                            <pr.Subtitle>
                                {ui.__("header.employment")}
                            </pr.Subtitle>
                            <EmploymentComponent
                                prefix="second_"
                                employment={
                                    this.props.user.secondBorrowerEmployment
                                }
                            />
                            <pr.Subtitle>
                                {ui.__("header.income.secondBorrower")}
                            </pr.Subtitle>
                            <IncomeSecondBorrower user={this.props.user} />
                        </pr.FormContainer>
                    </div>
                )}

                <pr.Separator />
                <pr.FormContainer>
                    <pr.Title>{ui.__("header.documents")}</pr.Title>
                    <DocumentsContent
                        user={this.props.user}
                        attachments={this.props.attachments}
                        isSummary={true}
                        invalidSubmit={this.invalidSubmit}
                    />
                </pr.FormContainer>

                <pr.Separator />
                <pr.FormContainer>

                    <FormsyCheckboxWithValidation
                        style={{ marginTop: theme.MARGIN }}
                        labelStyle={{
                            maxWidth:
                            Math.min(ui.windowWidth, theme.MAX_APP_WIDTH) *
                            0.8, // Fix for IE overflowing
                        }}
                        // validations="isTrue"
                        submitted={this.invalidSubmit}
                        // validationError={ui.__("error.requiredField")}
                        name="wantsClimateBonus"
                        label={ui.__("summary.climatebonus")}
                        defaultChecked={formWasSubmitted}
                    />

                    <FormsyCheckboxWithValidation
                        style={{ marginTop: theme.MARGIN }}
                        labelStyle={{
                            maxWidth:
                            Math.min(ui.windowWidth, theme.MAX_APP_WIDTH) *
                            0.8, // Fix for IE overflowing
                        }}
                        validations="isTrue"
                        submitted={this.invalidSubmit}
                        validationError={ui.__("error.requiredField")}
                        name="requirement"
                        label={ui.__("summary.requirement")}
                        defaultChecked={formWasSubmitted}
                    />

                    <FormsyCheckboxWithValidation
                        style={{ marginTop: theme.MARGIN }}
                        labelStyle={{
                            maxWidth:
                            Math.min(ui.windowWidth, theme.MAX_APP_WIDTH) *
                            0.8, // Fix for IE overflowing
                        }}
                        validations="isTrue"
                        submitted={this.invalidSubmit}
                        validationError={ui.__("error.requiredField")}
                        name="tax"
                        label={ui.__("summary.tax")}
                        defaultChecked={formWasSubmitted}
                    />

                    <FormsyCheckboxWithValidation
                        style={{ marginTop: theme.MARGIN }}
                        labelStyle={{
                            zIndex: 10,
                            maxWidth:
                            Math.min(ui.windowWidth, theme.MAX_APP_WIDTH) *
                            0.8, // Fix for IE overflowing
                        }}
                        validations="isTrue"
                        submitted={this.invalidSubmit}
                        validationError={ui.__("error.requiredField")}
                        name="termsAndConditions"
                        label={
                            <div>
                                {ui.__("summary.termsAndConditions.part1")}
                                <span
                                    style={{
                                        textDecoration: "underline",
                                        cursor: "pointer",
                                        color: "blue",
                                    }}
                                    onClick={() =>
                                        popupCenter(
                                            config.API_BASE_URL +
                                            "assets/RechtlicheHinweise.pdf",
                                            ui.__(
                                                "summary.termsAndConditions.part2"
                                            ),
                                            1024,
                                            768
                                        )
                                    }
                                >
                                    {ui.__("summary.termsAndConditions.part2")}
                                </span>
                                {ui.__("summary.termsAndConditions.part3")}
                            </div>
                        }
                        defaultChecked={formWasSubmitted}
                    />

                    <FormsyCheckboxWithValidation
                        style={{ marginTop: theme.MARGIN }}
                        labelStyle={{
                            zIndex: 10,
                            maxWidth:
                            Math.min(ui.windowWidth, theme.MAX_APP_WIDTH) *
                            0.8, // Fix for IE overflowing
                        }}
                        validations="isTrue"
                        submitted={this.invalidSubmit}
                        validationError={ui.__("error.requiredField")}
                        name="gdpr"
                        label={
                            <div>
                                {ui.__("summary.termsAndConditions.part1")}
                                <span
                                    style={{
                                        textDecoration: "underline",
                                        cursor: "pointer",
                                        color: "blue",
                                    }}
                                    onClick={() =>
                                        popupCenter(
                                            config.API_BASE_URL +
                                            "assets/Datenschutzhinweise.pdf",
                                            ui.__("summary.gdpr"),
                                            1024,
                                            768
                                        )
                                    }
                                >
                                    {ui.__("summary.gdpr")}
                                </span>
                                {ui.__("summary.termsAndConditions.part3")}
                            </div>
                        }
                        defaultChecked={formWasSubmitted}
                    />

                    <FormsyCheckboxWithValidation
                        style={{ marginTop: theme.MARGIN }}
                        labelStyle={{
                            maxWidth:
                            Math.min(ui.windowWidth, theme.MAX_APP_WIDTH) *
                            0.8, // Fix for IE overflowing
                        }}
                        validations="isTrue"
                        submitted={this.invalidSubmit}
                        validationError={ui.__("error.requiredField")}
                        name="customerOwnAccord"
                        label={ui.__("ownAccord")}
                        defaultChecked={formWasSubmitted}
                    />

                    <FormsyCheckboxWithValidation
                        style={{ marginTop: theme.MARGIN }}
                        labelStyle={{
                            maxWidth:
                            Math.min(ui.windowWidth, theme.MAX_APP_WIDTH) *
                            0.8, // Fix for IE overflowing
                        }}
                        validations="isTrue"
                        submitted={this.invalidSubmit}
                        validationError={ui.__("error.requiredField")}
                        name="creditInformation"
                        label={ui.__("summary.creditInformation")}
                        defaultChecked={formWasSubmitted}
                    />

                    {formHelpers.renderActionButton(
                        ui.__("summary.send"),
                        this.sendingMail,
                        undefined,
                        this.sendingMail
                    )}
                </pr.FormContainer>
            </Formsy.Form>
        );
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Summary.tsx